import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Partnerships = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  const { iphaLogo, pmiLogo, gcprLogo, mriiLogo, denstuLogo, gpBuddyLogo } = useStaticQuery(graphql`
    query {
      iphaLogo: file(relativePath: { eq: "shared/ipha.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      pmiLogo: file(relativePath: { eq: "shared/pmi.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      gcprLogo: file(relativePath: { eq: "shared/globalcom.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      mriiLogo: file(relativePath: { eq: "shared/mrii.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      denstuLogo: file(relativePath: { eq: "shared/denstu.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      gpBuddyLogo: file(relativePath: { eq: "shared/gp-buddy.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
  `);

  const partnerships = [
    {
      id: 1,
      name: 'IPHA',
      logo: iphaLogo,
      description:
        'Through science and innovation, we extend how long people live and how well they can live.',
      category: 'Healthcare Association'
    },
    {
      id: 2,
      name: 'PMI',
      logo: pmiLogo,
      description:
        'We provide education, upskilling and networking opportunities for the pharmaceutical industry.',
      category: 'Professional Development'
    },
    {
      id: 3,
      name: 'MRII',
      logo: mriiLogo,
      description:
        'Committed to professionalism and best practice for Healthcare Industry Representatives.',
      category: 'Professional Standards'
    },
    {
      id: 4,
      name: 'Global Com PR',
      logo: gcprLogo,
      description:
        'An alliance of 80 independent agencies worldwide specialising in marketing communication.',
      category: 'Global Network'
    },
    {
      id: 5,
      name: 'Dentsu',
      logo: denstuLogo,
      description:
        'Integrated growth and transformation partner nurturing innovations that drive outcomes.',
      category: 'Transformation Partner'
    },
    {
      id: 6,
      name: 'GP Buddy',
      logo: gpBuddyLogo,
      description:
        'Helps healthcare professionals find the services they require quickly and conveniently.',
      category: 'Digital Platform'
    }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    const cards = cardsRef.current;

    if (!section || !cards.length) return;

    gsap.set(cards, { opacity: 0, y: 40, scale: 0.95 });

    ScrollTrigger.create({
      trigger: section,
      start: 'top 70%',
      onEnter: () => {
        gsap.to(cards, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          stagger: 0.15,
          ease: 'power3.out'
        });
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <Section ref={sectionRef}>
      <HeaderSection>
        <SectionTitle>Our Strategic Partnerships</SectionTitle>
        <SectionSubtitle>
          Collaborating with industry leaders to deliver exceptional healthcare solutions
        </SectionSubtitle>
      </HeaderSection>

      <PartnershipsGrid>
        {partnerships.map((partnership, index) => (
          <PartnershipCard
            key={partnership.id}
            ref={(el) => {
              cardsRef.current[index] = el;
            }}>
            <CardHeader>
              <LogoContainer>
                <GatsbyImage image={getImage(partnership.logo)} alt={partnership.name} />
              </LogoContainer>
              <PartnerInfo>
                <PartnerName>{partnership.name}</PartnerName>
                <PartnerCategory>{partnership.category}</PartnerCategory>
              </PartnerInfo>
            </CardHeader>

            <CardContent>
              <PartnerDescription>{partnership.description}</PartnerDescription>
            </CardContent>
          </PartnershipCard>
        ))}
      </PartnershipsGrid>
    </Section>
  );
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    margin: 3rem 0;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h3`
  font-weight: 900;
  line-height: 1.1;
  font-size: 3rem;
  margin-bottom: 1rem;

  @media screen and (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const PartnershipsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PartnershipCard = styled.div`
  background: white;
  border-radius: 2px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: #e2e8f0;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
`;

const LogoContainer = styled.div`
  width: 100px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  flex-shrink: 0;

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
`;

const PartnerInfo = styled.div`
  flex: 1;
`;

const PartnerName = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
`;

const PartnerCategory = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
`;

const CardContent = styled.div`
  margin-top: 1rem;
`;

const PartnerDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #475569;
  margin: 0;
`;

export default Partnerships;
